import React from 'react';

import { FullscreenLayout } from '@sandtech-hub-monorepo/ui-kit';
import {
  Header,
  usePlatforms as getPlatforms,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { Footer } from '../app/Footer';
import { AppRoutesPaths } from '../routes/types';
import {
  supportUrlFor,
  useIsActiveUniversityStudent,
  useLearnerPath,
} from '../data/useLearnerPath';

interface Props {
  current: ReturnType<typeof getPlatforms>['current'];
  children: React.ReactNode;
}

/**
 * Split out of `RouteWithLayout` because it's the only branch that reads
 * `useIsActiveUniversityStudent` — a hook that requires `GuardedRoutes`'
 * context, which the other layouts (SignIn/SignUp's `Card`, etc.) render
 * outside of.
 */
export const FullscreenGuardedLayout = ({ current, children }: Props) => {
  const isActiveUniversityStudent = useIsActiveUniversityStudent();
  const path = useLearnerPath();

  // Payments is the university tuition-payments app — GSA has its own Stripe
  // checkout and never uses it. Support's route is always rewritten rather
  // than only for the non-active case, since `current.apps`' own Support
  // entry can point at a different domain per platform (e.g. SET's variants
  // use help.elmwoodinstitute.org).
  const apps = current.apps
    .filter((app) => isActiveUniversityStudent || app.name !== 'Payments')
    .map((app) => {
      if (app.name === 'Support') {
        return {
          ...app,
          route: supportUrlFor(isActiveUniversityStudent),
          // Outside the platform entirely, unlike every other tile here,
          // so it shouldn't navigate the learner away from what they were
          // doing.
          openInNewTab: true,
        };
      }
      if (app.name === 'Learning') {
        // `current.apps`' own Learning entry is the fully-qualified eHub URL,
        // for embedders (Savanna, Community, the header widget) that aren't
        // already on the eHub origin. Set-edu IS that origin, so this stays
        // an in-app route rather than a full navigation back to itself.
        return { ...app, route: AppRoutesPaths.Learning };
      }
      if (app.name === 'Community') {
        // `current.apps`' own Community entry is the external Circle
        // instance — correct for a degree learner. GSA (and a learner with
        // both a GSA and a degree enrollment) gets set-edu's own in-app
        // Community page instead.
        return path === 'degree'
          ? app
          : { ...app, route: AppRoutesPaths.Community };
      }
      return app;
    });

  return (
    <FullscreenLayout
      children={children}
      className="bg-background"
      header={
        <div className="print:hidden">
          <Header
            className="bg-card text-foreground shadow-none border-b border-border-secondary"
            sdk={false}
            homeRoute={'/'}
            withLogo={true}
            hasThemeSwitcher={false}
            apps={apps}
            showNotifications={false}
            showUserHeader={true}
            settingsApps={[
              /*
                TODO: We gotta extract these using the "getPlatforms"
                hook and make it accept local host too. And then we have to fix this
                for the central hub corresponding component. */
              {
                name: 'View profile',
                route: AppRoutesPaths.Profile,
                iconName: 'UserIcon',
                followedBySeparator: false,
              },
              {
                name: 'My Learning',
                route: AppRoutesPaths.Learning,
                iconName: 'GraduationCapIcon',
                followedBySeparator: false,
              },
              {
                name: 'Settings',
                route: AppRoutesPaths.Settings,
                iconName: 'SettingsIcon',
                // Carries the separator that used to follow Refer a
                // Friend, which was removed: it pointed at
                // `rewards${NX_COOKIE_DOMAIN}`, an ALX product with no
                // FIT equivalent, and degraded to the unresolvable
                // `https://rewards/refer-a-friend` wherever that variable
                // is unset.
                followedBySeparator: true,
              },
              {
                name: 'Support',
                route: supportUrlFor(isActiveUniversityStudent),
                iconName: 'HelpCircleIcon',
                followedBySeparator: true,
                openInNewTab: true,
              },
            ]}
            rewardsLink={undefined}
            logo={current.logo}
          />
        </div>
      }
      footer={
        <div className="print:hidden">
          <Footer />
        </div>
      }
    />
  );
};
